import React, { useEffect, useMemo, useState } from 'react'
import { Database, Save, Search, X } from '../icons'

import { humanize, parseCommaValues } from '../lib/queryWorkspaceUtils'

const STUDIO_TABS = [
  { value: 'vocabulary', label: 'Vocabulary' },
  { value: 'businessSemantic', label: 'Business Semantic' },
]

const SEMANTIC_TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'measure', label: 'Measure' },
  { value: 'dimension', label: 'Dimension' },
  { value: 'date', label: 'Date' },
]

export function MetadataStudioModal({
  embedded = false,
  onClose,
  catalog,
  metadata,
  synonyms,
  onSave,
  businessSemantic,
  onSaveBusinessSemantic,
}) {
  const [activeTab, setActiveTab] = useState('vocabulary')

  useEffect(() => {
    if (embedded) return undefined
    document.body.classList.add('semantic-modal-open')
    return () => document.body.classList.remove('semantic-modal-open')
  }, [embedded])

  const outerClass = embedded ? "relative p-0" : "semantic-metadata-studio-overlay fixed inset-0 z-[1000] bg-slate-900/40 p-4 backdrop-blur-sm"
  const panelClass = embedded ? "mx-auto flex h-[calc(100vh-250px)] min-h-[640px] max-w-[1520px] flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm" : "mx-auto flex h-[90vh] max-w-[1520px] flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl"

  return (
    <div className={outerClass}>
      <div className={panelClass}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-lg font-semibold text-slate-900">Metadata Studio</div>
            <div className="text-sm text-slate-500">Manage business vocabulary, autocomplete scope, and curated semantic definitions.</div>
          </div>
          {onClose ? (
            <button onClick={onClose} className="rounded-xl border border-slate-200 p-2 text-slate-600 hover:bg-slate-50">
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <div className="border-b border-slate-200 px-5 py-3">
          <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
            {STUDIO_TABS.map((tab) => {
              const active = activeTab === tab.value
              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                    active ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {activeTab === 'vocabulary' ? (
            <VocabularyStudio catalog={catalog} metadata={metadata} synonyms={synonyms} onSave={onSave} />
          ) : (
            <BusinessSemanticStudio payload={businessSemantic} onSave={onSaveBusinessSemantic} />
          )}
        </div>
      </div>
    </div>
  )
}

function VocabularyStudio({ metadata, synonyms, onSave }) {
  const [search, setSearch] = useState('')
  const [selectedKey, setSelectedKey] = useState('')

  const items = useMemo(() => {
    const flattened = []
    ;(metadata?.tables || []).forEach((table) => {
      ;(table?.measures || []).forEach((measure) => {
        flattened.push({
          key: measure.name,
          kind: 'measure',
          table: table.name,
          display: humanize(measure.name),
          description: measure.description || '',
          expression: measure.expression || '',
          synonyms: synonyms?.measures?.[measure.name] || [],
          formatString: measure.formatString || '',
        })
      })
      ;(table?.columns || []).forEach((column) => {
        const key = `${table.name}[${column.name}]`
        flattened.push({
          key,
          kind: 'dimension',
          table: table.name,
          display: humanize(column.name),
          description: column.description || '',
          expression: column.sourceColumn || '',
          synonyms: synonyms?.dimensions?.[key] || [],
          formatString: column.formatString || '',
        })
      })
    })
    return flattened
  }, [metadata, synonyms])

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter((item) =>
      [item.display, item.key, item.table, item.kind, ...(item.synonyms || [])].join(' ').toLowerCase().includes(q),
    )
  }, [items, search])

  useEffect(() => {
    if (!filteredItems.length) return
    if (!selectedKey || !filteredItems.some((item) => item.key === selectedKey)) {
      setSelectedKey(filteredItems[0].key)
    }
  }, [filteredItems, selectedKey])

  const selectedItem = filteredItems.find((item) => item.key === selectedKey) || filteredItems[0] || null
  const valueEntries = Object.entries(synonyms?.values || {})

  return (
    <div className="grid h-full min-h-0 gap-4 overflow-hidden p-4 xl:grid-cols-[360px_minmax(0,1fr)_340px]">
      <div className="min-h-0 overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50">
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Model fields</div>
        <div className="border-b border-slate-200 px-3 py-3">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search columns, measures, tables, or synonyms" className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
          </div>
        </div>
        <div className="h-full overflow-y-auto p-3">
          <div className="space-y-2">
            {filteredItems.map((item) => {
              const active = item.key === selectedKey
              const hasSyn = (item.synonyms || []).length > 0
              return (
                <button
                  key={item.key}
                  onClick={() => setSelectedKey(item.key)}
                  className={`w-full rounded-2xl border px-3 py-3 text-left transition ${active ? 'border-slate-900 bg-white shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50'} ${hasSyn ? 'text-slate-900' : 'text-slate-500'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`truncate text-sm ${hasSyn ? 'font-semibold' : 'font-normal'}`}>{item.display}</div>
                      <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-slate-400">{item.kind} · {item.table}</div>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-[10px] ${hasSyn ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{hasSyn ? `${item.synonyms.length} synonyms` : 'add'}</span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="min-h-0 overflow-hidden rounded-[24px] border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="text-base font-semibold text-slate-900">Field details</div>
          <div className="text-sm text-slate-500">Edit vocabulary used by autocomplete and the rule-based query engine.</div>
        </div>
        <div className="h-full overflow-y-auto p-5">
          {selectedItem ? (
            <MetadataDetailCard item={selectedItem} onSave={onSave} />
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500">Select a field to edit synonyms.</div>
          )}
        </div>
      </div>

      <div className="min-h-0 overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50">
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Value synonyms</div>
        <div className="h-full overflow-y-auto p-4">
          <div className="space-y-4">
            {valueEntries.map(([dimensionKey, mapping]) => (
              <div key={dimensionKey} className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className="mb-3 text-sm font-semibold text-slate-900">{humanize(dimensionKey)}</div>
                <div className="space-y-3">
                  {Object.entries(mapping).map(([value, valueSynonyms]) => (
                    <SynonymEditorCard
                      key={`${dimensionKey}-${value}`}
                      title={value}
                      subtitle={dimensionKey}
                      defaultValue={(valueSynonyms || []).join(', ')}
                      onSave={(synonymsText) => onSave('value', { canonical_name: dimensionKey, value, synonyms: parseCommaValues(synonymsText), replace: true })}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function BusinessSemanticStudio({ payload, onSave }) {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [draft, setDraft] = useState({ columns: [], items: [] })
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setDraft(payload || { columns: [], items: [] })
  }, [payload])

  const rows = useMemo(() => {
    return (draft?.items || []).map((item, index) => ({ item, index, semanticType: inferSemanticType(item) }))
  }, [draft])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(({ item, semanticType }) => {
      if (typeFilter !== 'all' && semanticType !== typeFilter) return false
      if (!q) return true
      const haystack = [
        item?.Display?.name,
        item?.Display?.internalName,
        ...(Array.isArray(item?.Synonym) ? item.Synonym : [item?.Synonym]),
        item?.Category,
        item?.Dataset,
        item?.Description,
        item?.Type,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [rows, search, typeFilter])

  function updateItem(index, updater) {
    setDraft((current) => {
      const items = [...(current?.items || [])]
      items[index] = updater(items[index])
      return { ...(current || {}), items }
    })
  }

  function updateField(index, key, value) {
    updateItem(index, (item) => ({ ...item, [key]: value }))
  }

  function updateDisplayField(index, key, value) {
    updateItem(index, (item) => ({ ...item, Display: { ...(item?.Display || {}), [key]: value } }))
  }

  async function handleSave() {
    setIsSaving(true)
    try {
      await onSave({ ...(draft || {}), items: (draft?.items || []).map(normalizeSemanticItem) })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden p-4">
      <div className="mb-4 rounded-[24px] border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <Database className="h-4 w-4" /> Business Semantic Catalog
            </div>
            <div className="mt-1 text-sm text-slate-500">Edit the curated semantic JSON that controls AI-visible fields, labels, and business synonyms.</div>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {isSaving ? 'Saving…' : 'Save business semantic'}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex min-w-[320px] flex-1 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search display, internal name, category, dataset, or synonym" className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
          </div>
          <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1">
            {SEMANTIC_TYPE_FILTERS.map((filter) => {
              const active = typeFilter === filter.value
              return (
                <button
                  key={filter.value}
                  onClick={() => setTypeFilter(filter.value)}
                  className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  {filter.label}
                </button>
              )
            })}
          </div>
          <div className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
            {filteredRows.length} / {rows.length} rows
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-[24px] border border-slate-200 bg-white">
        <table className="min-w-[1700px] w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-left text-slate-600">
            <tr>
              <Th>#</Th>
              <Th>Key</Th>
              <Th>Internal</Th>
              <Th>AI Mode</Th>
              <Th>KPI Type</Th>
              <Th>Display</Th>
              <Th>Internal Name</Th>
              <Th>Synonym</Th>
              <Th>Category</Th>
              <Th>Dataset</Th>
              <Th>Ordinal</Th>
              <Th>Description</Th>
              <Th>Format</Th>
              <Th>Type</Th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(({ item, index, semanticType }) => (
              <tr key={`${item?.Display?.internalName || item?.Display?.name || index}-${index}`} className="border-t border-slate-100 align-top">
                <Td className="text-slate-500">{index + 1}.<div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-slate-400">{semanticType}</div></Td>
                <Td><input type="checkbox" checked={Boolean(item?.Key)} onChange={(event) => updateField(index, 'Key', event.target.checked)} /></Td>
                <Td><input type="checkbox" checked={Boolean(item?.Internal)} onChange={(event) => updateField(index, 'Internal', event.target.checked)} /></Td>
                <Td><input type="checkbox" checked={Boolean(item?.['AI Mode'])} onChange={(event) => updateField(index, 'AI Mode', event.target.checked)} /></Td>
                <Td><input value={item?.['KPI Type'] || ''} onChange={(event) => updateField(index, 'KPI Type', event.target.value)} className="w-28 rounded-xl border border-slate-200 px-2 py-1.5" /></Td>
                <Td><input value={item?.Display?.name || ''} onChange={(event) => updateDisplayField(index, 'name', event.target.value)} className="w-40 rounded-xl border border-slate-200 px-2 py-1.5" /></Td>
                <Td><input value={item?.Display?.internalName || ''} onChange={(event) => updateDisplayField(index, 'internalName', event.target.value)} className="w-64 rounded-xl border border-slate-200 px-2 py-1.5 font-mono text-[12px]" /></Td>
                <Td><textarea value={Array.isArray(item?.Synonym) ? item.Synonym.join(', ') : item?.Synonym || ''} onChange={(event) => updateField(index, 'Synonym', parseCommaValues(event.target.value))} rows={2} className="w-48 rounded-xl border border-slate-200 px-2 py-1.5" /></Td>
                <Td><input value={item?.Category || ''} onChange={(event) => updateField(index, 'Category', event.target.value)} className="w-28 rounded-xl border border-slate-200 px-2 py-1.5" /></Td>
                <Td><input value={item?.Dataset || ''} onChange={(event) => updateField(index, 'Dataset', event.target.value)} className="w-28 rounded-xl border border-slate-200 px-2 py-1.5" /></Td>
                <Td><input type="number" value={item?.Ordinal ?? ''} onChange={(event) => updateField(index, 'Ordinal', event.target.value === '' ? null : Number(event.target.value))} className="w-24 rounded-xl border border-slate-200 px-2 py-1.5" /></Td>
                <Td><textarea value={item?.Description || ''} onChange={(event) => updateField(index, 'Description', event.target.value)} rows={2} className="w-72 rounded-xl border border-slate-200 px-2 py-1.5" /></Td>
                <Td><input value={item?.Format || ''} onChange={(event) => updateField(index, 'Format', event.target.value)} className="w-20 rounded-xl border border-slate-200 px-2 py-1.5" /></Td>
                <Td>
                  <select value={item?.Type || 'text'} onChange={(event) => updateField(index, 'Type', event.target.value)} className="w-28 rounded-xl border border-slate-200 px-2 py-1.5">
                    <option value="number">number</option>
                    <option value="text">text</option>
                    <option value="date">date</option>
                  </select>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Th({ children }) {
  return <th className="border-b border-slate-200 px-3 py-3 text-xs font-semibold uppercase tracking-[0.12em]">{children}</th>
}

function Td({ children, className = '' }) {
  return <td className={`px-3 py-3 ${className}`}>{children}</td>
}

function MetadataDetailCard({ item, onSave }) {
  const [value, setValue] = useState((item.synonyms || []).join(', '))
  useEffect(() => setValue((item.synonyms || []).join(', ')), [item])

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{item.kind}</div>
        <div className={`mt-2 text-2xl ${item.synonyms?.length ? 'font-bold text-slate-900' : 'font-medium text-slate-600'}`}>{item.display}</div>
        <div className="mt-2 text-sm text-slate-500">Table: {item.table}</div>
        <div className="mt-1 text-sm text-slate-500">Key: {item.key}</div>
        {item.expression ? <pre className="mt-3 overflow-x-auto whitespace-pre-wrap rounded-2xl bg-white p-3 text-[11px] text-slate-700">{item.expression}</pre> : null}
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-2 text-sm font-semibold text-slate-900">Synonyms</div>
        <div className="mb-3 text-sm text-slate-500">Comma-separated words or phrases that business users type in the chat box.</div>
        <textarea value={value} onChange={(event) => setValue(event.target.value)} rows={5} className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400" placeholder="employee, rep, sales rep" />
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => onSave(item.kind, { canonical_name: item.key, synonyms: parseCommaValues(value), replace: true })}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white"
          >
            <Save className="h-3.5 w-3.5" /> Save synonyms
          </button>
        </div>
      </div>
    </div>
  )
}

function SynonymEditorCard({ title, subtitle = '', defaultValue = '', onSave }) {
  const [value, setValue] = useState(defaultValue)
  useEffect(() => setValue(defaultValue), [defaultValue])
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="mb-2 text-sm font-semibold text-slate-900">{title}</div>
      {subtitle ? <div className="mb-2 text-xs text-slate-500">{subtitle}</div> : null}
      <textarea value={value} onChange={(event) => setValue(event.target.value)} rows={3} className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400" placeholder="Comma separated synonyms" />
      <div className="mt-3 flex justify-end">
        <button onClick={() => onSave(value)} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white">
          <Save className="h-3.5 w-3.5" /> Save
        </button>
      </div>
    </div>
  )
}

function inferSemanticType(item) {
  const internalName = String(item?.Display?.internalName || '').toUpperCase()
  const type = String(item?.Type || '').toLowerCase()
  if (type === 'date') return 'date'
  if (internalName.startsWith('MEASURE TABLE.') || internalName.startsWith('IQVIA') && type === 'number') return 'measure'
  return 'dimension'
}

function normalizeSemanticItem(item) {
  return {
    ...item,
    Synonym: Array.isArray(item?.Synonym) ? item.Synonym : parseCommaValues(item?.Synonym || ''),
    Display: {
      name: item?.Display?.name || '',
      internalName: item?.Display?.internalName || '',
    },
  }
}
